import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import dayjs from "dayjs";
import { listQuotes, getQuote } from "./api/quotes";
import QuoteEditor from "./QuoteEditor";

const STATUS_FILTERS = ["ALL", "DRAFT", "SENT", "ACCEPTED", "REJECTED"];

const statusColor = (s) => {
  switch (s) {
    case "ACCEPTED": return "success";
    case "SENT": return "info";
    case "REJECTED": return "error";
    default: return "default";
  }
};

export default function StaticQuotesTab() {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("ALL");
  const [editorOpen, setEditorOpen] = useState(false);
  const [activeQuote, setActiveQuote] = useState(null);

  const fetchAll = useCallback(() => {
    setLoading(true);
    setError("");
    listQuotes()
      .then((d) => setQuotes(Array.isArray(d) ? d : []))
      .catch((e) => setError(e.message || "Failed to load quotes"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return quotes.filter((row) => {
      if (status !== "ALL" && row.status !== status) return false;
      if (!q) return true;
      return (
        (row.title || "").toLowerCase().includes(q) ||
        (row.clientName || "").toLowerCase().includes(q) ||
        (row.createdByName || "").toLowerCase().includes(q)
      );
    });
  }, [quotes, search, status]);

  const openExisting = async (id) => {
    try {
      const full = await getQuote(id);
      setActiveQuote(full);
      setEditorOpen(true);
    } catch (e) {
      setError(e.message || "Failed to load quote");
    }
  };

  const handleSaved = (saved) => {
    if (saved === null) {
      setEditorOpen(false);
      setActiveQuote(null);
    } else if (saved) {
      setActiveQuote(saved);
    }
    fetchAll();
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }} sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ flexGrow: 1 }}>Quotes</Typography>
        <TextField
          size="small"
          label="Search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Title, client, author"
        />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Status</InputLabel>
          <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUS_FILTERS.map((s) => (
              <MenuItem key={s} value={s}>{s}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button startIcon={<RefreshIcon />} onClick={fetchAll}>Refresh</Button>
      </Stack>

      {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Ref</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Client</TableCell>
              <TableCell>Author</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">No quotes found</TableCell>
              </TableRow>
            ) : (
              filtered.map((row) => (
                <TableRow
                  key={row.id}
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={() => openExisting(row.id)}
                >
                  <TableCell>Q-{row.id}</TableCell>
                  <TableCell>{row.quoteDate ? dayjs(row.quoteDate).format("DD/MM/YYYY") : ""}</TableCell>
                  <TableCell>{row.title}</TableCell>
                  <TableCell>{row.clientName}</TableCell>
                  <TableCell>{row.createdByName || ""}</TableCell>
                  <TableCell>
                    <Chip size="small" label={row.status} color={statusColor(row.status)} />
                  </TableCell>
                  <TableCell align="right">
                    {(row.currency === "EUR" ? "€" : row.currency === "USD" ? "$" : "£")} {(Number(row.total) || 0).toFixed(2)} {row.currency || "GBP"}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <QuoteEditor
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        quote={activeQuote}
        onSaved={handleSaved}
      />
    </Box>
  );
}
